import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import { Box, TextField, Typography } from '@material-ui/core'
import HotSwapSummaryDetails from './components/how-swap-summary-details'
import {
    hotswapStatusOptions,
    RESPONSIBLE_OR_TRACKING
} from './constants/constants'
import { useParams } from 'react-router-dom'
import { UserPermissions } from '../../services/permissions'
import CustomButton from '../root/components/common/button'
import { Loading, Picker } from '../root/components/common'
import { NavLink } from 'react-router-dom'
import * as actions from './actions'
import moment from 'moment'
import HotSwapAccordionDetails from './components/hot-swap-accordion-details'
import { ExportFileIcon } from '../root/components/common/export-icon'
import { LOCAL_LOCALE_KEY } from '../root/constants'
import { getHotswapFileForExport } from './api/hotswaps-api'
import { enqueueSnackbar } from '../root/actions'
import { composeFileName, downloadFile } from 'web/src/utils'
import HotSwapDropdownCarrier from './components/hot-swap-dropdown-carrier'

export default function HotSwapDetails() {
    const [responsibleOrTracking, setResponsibleOrTracking] = useState('')
    const [isExporting, setIsExporting] = useState(false)
    const [carrier, setCarrier] = useState()
    const [hotswapStatus, setHotswapStatus] = useState('')

    const { locale, theme } = useSelector(state => state.main)
    const { hotSwap, isLoading } = useSelector(state => state.hotswap)

    const { hotswapId } = useParams()
    const classes = useStyles()
    const dispatch = useDispatch()

    useEffect(() => {
        fetchHotswap(hotswapId)
    }, [hotswapId])

    const fetchHotswap = hotswapId => {
        dispatch(actions.getHotSwapByIdRequest(hotswapId))
    }

    const exportHotswap = async fileType => {
        setIsExporting(true)
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const params = { fileType, locale_key }
        const blob = await getHotswapFileForExport(hotswapId, params)
        if (blob) {
            const fileName = composeFileName('hotswap_export', fileType)
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDeviceReportsEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        setIsExporting(false)
    }

    const isAdmin = useMemo(() => {
        return UserPermissions.isTraplinkedAdmin()
    })

    const showCarrierDropDown = useMemo(() => {
        return hotSwap?.hotswap_type === 'swap_by_user'
    }, [hotSwap])

    const onSaveClick = () => {
        if (hotSwap) {
            const body = { carrier }
            body[RESPONSIBLE_OR_TRACKING[hotSwap.hotswap_type]] =
                responsibleOrTracking

            dispatch(actions.updateHotSwapRequest(hotswapId, body))
        }
    }

    const onSaveStatus = () => {
        const body = {
            status: hotswapStatus
        }
        dispatch(actions.updateHotSwapRequest(hotswapId, body))
    }

    const onChangeResponsibleOrTracking = e =>
        setResponsibleOrTracking(e.target.value)

    useEffect(() => {
        if (hotSwap) {
            setHotswapStatus(hotSwap.status)
        }
    }, [hotSwap])

    return (
        <div className={classes.container}>
            {(isExporting || isLoading) && (
                <div className={classes.loader}>
                    <Loading />
                </div>
            )}
            {hotSwap && !isLoading && (
                <div
                    className={`${classes.wrapper} ${
                        isAdmin ? classes.fullW : classes.wrapperWithVideo
                    } ${isExporting ? classes.disabledWrapper : ''}`}>
                    <Box className={classes.headingSection} style={{ gap: 0 }}>
                        <Typography className={classes.heading}>
                            <div className={classes.headingText}>
                                {hotSwap.name}
                                <span className={classes.labelWrapper}>
                                    {hotSwap.status}
                                </span>
                            </div>

                            <div onClick={() => exportHotswap('pdf')}>
                                <ExportFileIcon
                                    fileType="pdf"
                                    size="regular"
                                    locale={locale}
                                />
                            </div>
                        </Typography>

                        <Typography className={classes.text}>
                            {hotSwap.customer_name}
                        </Typography>
                    </Box>
                    <HotSwapAccordionDetails
                        locale={locale}
                        theme={theme}
                        hotSwap={hotSwap}
                        isAdmin={isAdmin}
                        exportHotswap={exportHotswap}
                    />
                    <Box className={classes.detailsSection}>
                        <Typography className={classes.text}>
                            {
                                locale.hotSwap[hotSwap?.hotswap_type]
                                    .detailsPageText
                            }
                        </Typography>
                        <Box className={classes.cardContainer}>
                            <HotSwapSummaryDetails
                                formData={hotSwap}
                                scheduledDate={hotSwap.date}
                                scheduledTime={moment(
                                    hotSwap.time,
                                    'HH:mm:ss'
                                ).format('HH:mm')}
                                swapType={hotSwap?.hotswap_type}
                                locale={locale}
                                hotswapId={hotswapId}
                                theme={theme}
                                isAdmin={isAdmin}
                                fetchHotswap={fetchHotswap}
                            />
                        </Box>
                        {isAdmin && hotSwap.note ? (
                            <div
                                style={{
                                    width: 300
                                }}>
                                ***
                                <br />
                                {hotSwap.note}
                            </div>
                        ) : null}
                    </Box>
                    {isAdmin && (
                        <>
                            <br />
                            <div className={classes.superAdminContainer}>
                                {locale.devices.status}
                                <div className={classes.formContainer}>
                                    <Picker
                                        onChange={setHotswapStatus}
                                        value={hotswapStatus}
                                        options={hotswapStatusOptions}
                                    />
                                    <CustomButton onClick={onSaveStatus}>
                                        {locale.common.save}
                                    </CustomButton>
                                </div>
                            </div>
                            <br />
                            <div className={classes.superAdminContainer}>
                                {
                                    locale.hotSwap[hotSwap?.hotswap_type]
                                        .footerText
                                }
                                <div className={classes.formContainer}>
                                    <TextField
                                        hiddenLabel
                                        value={responsibleOrTracking}
                                        onChange={onChangeResponsibleOrTracking}
                                    />
                                    {showCarrierDropDown && (
                                        <HotSwapDropdownCarrier
                                            value={carrier}
                                            onItemChange={setCarrier}
                                        />
                                    )}
                                    <CustomButton onClick={onSaveClick}>
                                        {locale.common.save}
                                    </CustomButton>
                                </div>
                                <NavLink
                                    to={`/swap-replacements?hotswapId=${hotswapId}&hotswapLocation=${hotSwap.name}`}>
                                    {locale.hotSwap.swapReplacment}
                                </NavLink>
                            </div>
                        </>
                    )}
                </div>
            )}
            {!isAdmin && (
                <div className={classes.wrapper}>
                    <Box
                        className={classes.headingSection}
                        style={{ marginTop: 135 }}>
                        <Typography className={classes.text}>
                            HotSwap Video Manual
                        </Typography>
                        <iframe
                            className={classes.videoTag}
                            src={`https://www.youtube.com/embed/QyrMMIysYoM`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            // eslint-disable-next-line
                            allowFullScreen
                            title="test"
                        />
                    </Box>
                </div>
            )}
        </div>
    )
}

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        justifyContent: 'space-around'
    },
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: 'fit-content',
        maxWidth: '100%'
    },
    fullW: {
        minWidth: '100%'
    },
    wrapperWithVideo: {
        maxWidth: '50%'
    },
    headingSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
        padding: '32px 0'
    },
    detailsSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
        padding: '32px 0',
        width: 'fit-content'
    },
    heading: {
        fontSize: '24.5px',
        lineHeight: '31px',
        fontWeight: 600,
        display: 'flex',
        justifyContent: 'space-between'
    },
    headingText: {
        display: 'flex',
        gap: '30px'
    },
    text: { fontSize: '1rem', lineHeight: '20px' },
    cardContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: '30px 25px 25px 25px',
        minWidth: '340px',
        gap: '1rem',
        borderRadius: theme.borderRadius,
        border: '1px solid #EAECF0',
        boxShadow:
            '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)'
    },
    videoTag: {
        width: '100%',
        height: '100%',
        maxHeight: '200px',
        borderRadius: '8px',
        border: 'none',
        outline: 'none'
    },
    labelWrapper: {
        borderRadius: '16px',
        fontWeight: 500,
        fontSize: '16px',
        padding: '4px 12px 4px 12px',
        color: theme.blue,
        backgroundColor: theme.blueLight
    },
    formContainer: {
        display: 'flex',
        gap: 16
    },
    justifyEnd: {
        justifyContent: 'flex-end'
    },
    loader: {
        top: '50vh',
        zIndex: 9,
        position: 'absolute',
        left: 0,
        right: 0,
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    disabledWrapper: {
        opacity: 0.3,
        pointerEvents: 'none'
    }
}))
